import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import audio from '../assets/images/audio.png';
import { useLanguage } from '../contexts/LanguageContext';

const MainWrapper = styled.div`
  background: transparent;
  color: white;
`;

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  @media (max-width: 768px) {
    height: auto;
    min-height: 45vh;
  }
`;

// Legibility over the video - not a scrim, glass panel, text-shadow, or
// outline (all tried and dropped) but a solid, opaque "safe space": same
// color as the SubElement cards below (#040A19), flush to the left edge,
// sharp corners (no border-radius). Full-height so it reads as a fixed panel
// rather than a floating box, with the text vertically centered inside it.
//
// $scrollFade (0-1, driven by scroll position - see SlideShowContent) fades
// the panel itself away as the visitor scrolls, so the video isn't blocked
// once they've started moving past the hero. Only the background fades, not
// the text/button/etc - those are children with their own opaque colors.
const HeroContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 30%;
  height: 100%;
  padding: 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: rgba(4, 10, 25, ${props => 1 - props.$scrollFade});
  z-index: 3;
  @media (max-width: 768px) {
    position: relative;
    width: 100%;
    height: auto;
    top: auto;
    left: auto;
    padding: 20px;
    background: #040A19;
  }
`;

const HeroButton = styled.button`
  background-color: #47ABDB;
  text-decoration: none;
  height: 4.0rem;
  font-weight: bold;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 1.8rem;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  max-width: 300px;
  text-align: center;
  margin-bottom: 0.1rem;

  &:hover {
    background-color: #0056b3;
  }
  @media (max-width: 1080px) {
    font-size: 1.4rem;;
  }
  @media (max-width: 768px) {
    font-size: 1.4rem;;
  }
`;

const ConferenceInfo = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const ConferenceTitle = styled.p`
  font-size: 1.4rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  @media (max-width: 768px) {
    font-size: 1rem;;
  }
`;

const ConferenceDetails = styled.p`
  font-size: 1.4rem;
  margin: 0;
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HeroMedia = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  @media (max-width: 768px) {
    position: relative;
    height: 74vh;
    overflow: hidden;
  }
`;

// Duplicates the headline block for narrow screens, since HeroContent drops
// out of absolute positioning on mobile (see its media query) and moves below
// the video instead - matches heartport-landing's MobileHeadlineOverlay.
// On mobile this is rendered in normal flow ABOVE the video (see
// SlideShowContent), never over it, so the hero stacks as
// [headline panel] [full video] [text panel] and the video is fully visible;
// it carries the same solid #040A19 "safe space" treatment as HeroContent
// and the SubElement cards below.
const MobileHeadlineOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    padding: 1.5rem 20px;
    box-sizing: border-box;
    background: #040A19;
  }
`;

const MediaItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.active ? 1 : 0};
  transform: translateX(${props => props.position}%);
  transition: opacity 0.0s ease-in-out, transform 1.5s ease-in-out;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    video {
      transform: scale(1.2);
      transform-origin: top center;
    }
  }
`;

const SubElementsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Tile layout mirrors heartport-landing's hero sub-elements: no fixed card
// height (content defines it), fixed 200px image, content column with a gap
// so a wrapped description grows the card instead of being squeezed.
const SubElement = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  background: #040A19;
  padding: 8px;
  ${props => props.isConference && `
    outline: 10px solid #90C4D9;
    outline-offset: -2px;
  `}
`;

const SubElementImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const SubElementImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${SubElement}:hover & {
    transform: scale(1.1);
  }
`;

const SubElementContent = styled.div`
  padding: 10px 2px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ComingSoonBanner = styled.div`
  position: absolute;
  top: 30px;
  right: -50px;
  background-color: #47ABDB;
  color: white;
  padding: 5px 0;
  width: 200px;
  text-align: center;
  transform: rotate(45deg);
  font-size: 0.6em;
  font-weight: bold;
  z-index: 2;
  display: ${props => props.show ? 'block' : 'none'};
`;

const LinkTitle = styled.span`
  color: white;
  font-weight: bold;
  font-size: 1.4rem;
`;

// Same as heartport-landing: up to three wrapped lines (the "line break"
// behaviour the customer pointed at) instead of a single truncated line.
const LinkDescription = styled.span`
  color: white;
  font-size: 1.05rem;
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const AudioIcon = styled.img`
  width: 30px;
  height: 25px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  margin-top: 10px;
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.clickable ? 'auto' : 'none'};
  transition: opacity 0.3s ease;

  @media (max-width: 768px) {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 3;
    margin-top: 0;
  }
`;

// Copy is heartport-landing's, but sized for this project's narrow left-hand
// column (HeroContent above) instead of landing's full-width hero - landing's
// own font sizes (up to 5.5rem) were built for text spanning most of the
// screen and were way oversized once confined to a 30%-wide column.

const WelcomeSmall = styled.div`
  color: #48ABDB;
  font-size: 1.1rem;
  margin-bottom: 0.2rem;
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const WelcomeLarge = styled.div`
  color: #48ABDB;
  font-size: 2.4rem;
  font-weight: bold;
  line-height: 1.1;
  margin-bottom: 0.4rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Same size as ScienceCaresFirstWord ("Because at HEARTPORT,") - both are
// small setup lines rather than headline-scale text.
const BigHeadline = styled.h1`
  font-size: 0.85rem;
  line-height: 1.15;
  white-space: normal;
  margin-bottom: 0.6rem;
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ColoredWord = styled.span`
  color: #48ABDB;
`;

const SideText = styled.div`
  max-width: 100%;
`;

const SubHeadline = styled.p`
  font-size: 1rem;
  font-weight: bold;
  color: white;
  margin-bottom: 0.7rem;
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const HeroParagraph = styled.p`
  font-size: 0.85rem;
  color: white;
  opacity: 0.92;
  margin-bottom: 0.35rem;
  line-height: 1.4;
  @media (max-width: 768px) {
    font-size: 1.15rem;
    line-height: 1.5;
  }
`;

const ScienceCares = styled.h2`
  color: #48ABDB;
  margin-bottom: 0.6rem;
`;

const ScienceCaresFirstWord = styled.span`
  font-size: 0.85rem;
  display: block;
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ScienceCaresRest = styled.span`
  font-size: 1.5rem;
  display: block;
  @media (max-width: 768px) {
    font-size: 2.1rem;
  }
`;

const SlideShowContent = ({ heroContent = {}, subElements = [], conference = null, openRegistrationModal }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenVideoIndex, setFullscreenVideoIndex] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const API_URL = process.env.REACT_APP_API_URL || '';
  const videoRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // 0 = panel fully opaque (top of page), 1 = fully faded away - lets the
  // video show through unobstructed once the visitor starts scrolling,
  // rather than keeping the solid panel up for the whole time the hero is
  // still on screen.
  const [scrollFade, setScrollFade] = useState(0);
  const { language } = useLanguage();

  const slideShowItems = heroContent?.slideShowItems?.filter(item => item && item.mediaType) || [];

  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, slideShowItems.length);
  }, [slideShowItems]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const FADE_DISTANCE_PX = 400;
    const handleScroll = () => {
      setScrollFade(Math.min(window.scrollY / FADE_DISTANCE_PX, 1));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const currentVideo = videoRefs.current[currentSlideIndex];
    if (currentVideo) {
      currentVideo.playsInline = true;
      currentVideo.muted = isMuted;
      currentVideo.autoplay = true;
      currentVideo.loop = true;

      currentVideo.play().catch(error => console.error('Error attempting to play', error));
    }
  }, [currentSlideIndex, isMuted]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        video.muted = index !== fullscreenVideoIndex;
      }
    });
  }, [fullscreenVideoIndex, currentSlideIndex]);

  useEffect(() => {
    if (slideShowItems.length > 1) {
      const interval = setInterval(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentSlideIndex((prevIndex) => {
            const newIndex = (prevIndex + 1) % slideShowItems.length;
            if (isMobile) {
              setIsMuted(true);
            }
            return newIndex;
          });
          setIsTransitioning(false);
        }, 1500);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [slideShowItems, isMobile]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const newFullscreenState = !!document.fullscreenElement;
      setIsFullscreen(newFullscreenState);

      if (newFullscreenState) {
        setFullscreenVideoIndex(currentSlideIndex);
        setIsMuted(false);
      } else {
        setFullscreenVideoIndex(null);
        setIsMuted(true);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [currentSlideIndex]);

  const formatDate = (dateString) => {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  const getSlidePosition = (index) => {
    const totalSlides = slideShowItems.length;

    if (index === currentSlideIndex) return isTransitioning ? -100 : 0;

    const nextIndex = (currentSlideIndex + 1) % totalSlides;
    if (index === nextIndex) return isTransitioning ? 0 : 100;

    return 100;
  };

  const handleSubElementClick = (element) => {
    const isHeartportPublic = element.title.toLowerCase() === 'heartport public';
    const isHeartsConference = element.title.toLowerCase() === 'hearts conference' || element.title.toLowerCase() === 'hearts konferenz';

    if (element.externalUrl && (!element.showComingSoon || isHeartportPublic) && !isHeartsConference) {
      window.open(element.externalUrl, '_blank');
    }
  };

  const currentSlide = slideShowItems[currentSlideIndex] || {};
  const isCurrentSlideVideo = currentSlide.mediaType === 'VIDEO';

  const handleAudioToggle = () => {
    const currentVideo = videoRefs.current[currentSlideIndex];
    if (currentVideo) {
      if (isMobile) {
        setIsMuted(!isMuted);
      } else {
        if (!isFullscreen) {
          if (currentVideo.requestFullscreen) {
            currentVideo.requestFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          }
        }
      }
    }
  };

  const renderAudioIcon = () => (
    <AudioIcon
      src={audio}
      alt={isMuted ? "Enable audio" : "Disable audio"}
      onClick={isCurrentSlideVideo ? handleAudioToggle : undefined}
      visible={isCurrentSlideVideo}
      clickable={isCurrentSlideVideo}
    />
  );

  const getContent = (defaultContent, enContent) => {
    return language === 'en' && enContent ? enContent : (defaultContent || '');
  };

  // Static per request - was previously authored through the hero-section CMS
  // (bigHeadline/subHeadline/scienceCares); the video background stays
  // CMS-driven (heroContent.slideShowItems below), only this copy is fixed.
  const renderHeroContent = () => {
    return (
      <>
        {!isMobile && (
          <>
            <WelcomeSmall>Welcome at</WelcomeSmall>
            <WelcomeLarge>HEARTPORT</WelcomeLarge>
            <BigHeadline><ColoredWord>DEMOCRATIZING SCIENCE</ColoredWord></BigHeadline>
            <SubHeadline>HEARTPORT is your portal for maximizing life through the Art of Medical Science.</SubHeadline>
          </>
        )}
        <SideText>
          <HeroParagraph>
            We make science actionable and transformative at our conferences and across our digital platforms. For Doctors. For the Public.
          </HeroParagraph>
          <HeroParagraph>
            Medicine and Art have one thing in common:<br />They refuse the limits of nature – to go beyond.
          </HeroParagraph>
          <HeroParagraph>
            We are medicine through communication.
          </HeroParagraph>
          <ScienceCares>
            <ScienceCaresFirstWord>Because at HEARTPORT,</ScienceCaresFirstWord>
            <ScienceCaresRest>SCIENCE CARES.</ScienceCaresRest>
          </ScienceCares>
        </SideText>
        {conference && (
          <HeroButton onClick={() => openRegistrationModal(conference)}>
            <FormattedMessage
              id={conference.requiresApproval ? "hero.button.pre" : "hero.button"}
              defaultMessage={conference.requiresApproval ? "PRE-REGISTER" : "Register"}
            />
          </HeroButton>
        )}
        {conference && (
          <ConferenceInfo>
            <ConferenceTitle>
              <>{getContent(conference?.mainHeadline, conference?.mainHeadlineEN) || ''}</>
            </ConferenceTitle>
            <ConferenceDetails>
              {formatDate(conference.dateTime) && conference.location ? (
                  <>
                    {formatDate(conference.dateTime)} - {conference.location}
                  </>
                ) : (
                  <>
                    {formatDate(conference.dateTime)}
                    {conference.location}
                  </>
              )}
            </ConferenceDetails>
          </ConferenceInfo>
        )}
        {!isMobile && renderAudioIcon()}
      </>
    );
  };

  return (
    <MainWrapper>
      <HeroSection>
        {isMobile && (
          <MobileHeadlineOverlay>
            <WelcomeSmall>Welcome at</WelcomeSmall>
            <WelcomeLarge>HEARTPORT</WelcomeLarge>
            <BigHeadline><ColoredWord>DEMOCRATIZING SCIENCE</ColoredWord></BigHeadline>
            <SubHeadline>HEARTPORT is your portal for maximizing life through the Art of Medical Science.</SubHeadline>
          </MobileHeadlineOverlay>
        )}
        <HeroMedia>
          {slideShowItems.map((slide, index) => (
            <MediaItem
              key={slide.id || index}
              active={index === currentSlideIndex || (isTransitioning && index === (currentSlideIndex + 1) % slideShowItems.length)}
              position={getSlidePosition(index)}
            >
              {slide.mediaType === 'VIDEO' ? (
                <video
                  ref={el => videoRefs.current[index] = el}
                  playsInline
                  muted={index !== fullscreenVideoIndex}
                  autoPlay={index === currentSlideIndex}
                  loop
                  controls={isFullscreen && index === fullscreenVideoIndex}
                >
                  <source src={`${API_URL}${slide.mediaUrl}`} type="video/mp4" />
                </video>
              ) : (
                <img src={`${API_URL}${slide.mediaUrl}`} alt="" />
              )}
            </MediaItem>
          ))}
          {isMobile && renderAudioIcon()}
        </HeroMedia>
        <HeroContent $scrollFade={scrollFade}>
          {renderHeroContent()}
        </HeroContent>
      </HeroSection>
      <SubElementsSection>
        {subElements.map((element, index) => {
          const isHeartportPublic = element.title.toLowerCase() === 'heartport public';
          const isHeartsConference = element.title.toLowerCase() === 'hearts conference' || element.title.toLowerCase() === 'hearts konferenz';

          return (
            <SubElement
              key={index}
              isConference={isHeartsConference}
              isClickable={(!element.showComingSoon || isHeartportPublic) && !isHeartsConference && element.externalUrl}
              onClick={() => handleSubElementClick(element)}
            >
              <SubElementImageWrapper>
                <SubElementImage src={element.url} alt={element.title} />
              </SubElementImageWrapper>
              <SubElementContent>
                <LinkTitle>{element.title}</LinkTitle>
                <LinkDescription>{element.description}</LinkDescription>
              </SubElementContent>
              <ComingSoonBanner show={!isHeartportPublic && !isHeartsConference && element.showComingSoon}>
                COMING SOON
              </ComingSoonBanner>
            </SubElement>
          );
        })}
      </SubElementsSection>
    </MainWrapper>
  );
};

export default SlideShowContent;
